import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc } from 'firebase/firestore';
import {
  Box,
  ChevronDown,
  ImageIcon,
  SlidersHorizontal,
  Sparkles,
  Upload,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useIsInsideAppLayout } from '../components/AppLayoutContext';
import Seo from '../seo/Seo';
import { buildPublicToolPageSchemas } from '../seo/structuredData';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import { useTrialGenerationUsed } from '../hooks/useTrialGenerationUsed';
import NewDesignSelect from '../components/NewDesignSelect';
import VerticalAiBeforeAfterSlider from '../components/VerticalAiBeforeAfterSlider';
import DesignOutputWorkspace from '../components/designWorkspace/DesignOutputWorkspace';
import DesignResultViewer from '../components/designWorkspace/DesignResultViewer';
import ToolSeoSections from '../components/ToolSeoSections';
import CollapsibleSeoContent from '../components/seo/CollapsibleSeoContent';
import { getSeoContent, getSeoFaqSchema } from '../seo/seoContentData';
import { useDesignWorkspaceMode } from '../hooks/useDesignWorkspaceMode';
import { API_BASE_URL, HAS_API_URL } from '../config/api';
import { db } from '../firebase';
import { fetchProjectOriginalBlob } from '../lib/fetchProjectOriginalImage';
import { requestFloorPlanTo3DImage } from '../lib/requestFloorPlanTo3DImage';
import { buildProjectTitle } from '../lib/projectWorkspaceUtils';
import { downloadUrlAsFile } from '../lib/downloadUrlAsFile';
import { resolveEditSourcePreviewUrl } from '../lib/projectWorkspaceUtils';
import { applyPreviewImageLayoutClasses } from '../lib/workspacePreviewImage';
import { prepareFloorPlanTo3dInputImage } from '../lib/prepareFloorPlanTo3dInputImage';
import {
  FLOOR_PLAN_3D_CAMERA_VIEW_DEFAULT,
  FLOOR_PLAN_3D_CAMERA_VIEW_OPTIONS,
  getFloorPlan3dCameraViewPopupOptions,
  normalizeFloorPlan3dCameraViewForUi,
} from '../data/floorPlan3dCameraViewOptions';
import {
  FLOOR_PLAN_3D_OUTPUT_TYPE_DEFAULT,
  FLOOR_PLAN_3D_OUTPUT_TYPE_OPTIONS,
} from '../data/floorPlan3dOutputTypeOptions';
import {
  FLOOR_PLAN_3D_FURNISHING_DEFAULT,
  FLOOR_PLAN_3D_FURNISHING_OPTIONS,
} from '../data/floorPlan3dFurnishingOptions';
import { DESIGN_STYLES } from '../lib/roomDesignOptions';
import floorPlan3dPreviewBefore from '../assets/design_tools/2d-to-3d-floorplan-before-H.webp';
import floorPlan3dPreviewAfter from '../assets/design_tools/2d-to-3d-floorplan-after-H.webp';

const WORKSPACE_PREVIEW = {
  alt: '2D to 3D floor plan preview',
  hint: 'Convert flat floor plans into clearer 3D-style previews for layout planning.',
  beforeImage: floorPlan3dPreviewBefore,
  afterImage: floorPlan3dPreviewAfter,
};
const QUALITY_STANDARD = 'standard';
const QUALITY_HQ = 'hq';
const NOTES_MAX = 200;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * @param {'standard' | 'hq'} quality
 * @param {boolean | null} trialGenerationUsed
 * @returns {number}
 */
function effectiveCreditCost(quality, trialGenerationUsed) {
  if (quality === QUALITY_HQ) return 4;
  if (trialGenerationUsed === false) return 0;
  return 2;
}

/**
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @param {unknown} value
 * @param {readonly { value: string }[]} options
 * @param {string} fallback
 * @returns {string}
 */
function pickOptionValue(value, options, fallback) {
  const allowed = options.map((option) => option.value);
  return typeof value === 'string' && allowed.includes(value) ? value : fallback;
}

function FloorPlanTo3DImageTestPage({ variant = 'test', publicSeo = null }) {
  const isInsideAppLayout = useIsInsideAppLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editFromUrl = useMemo(() => {
    const raw = searchParams.get('edit');
    return typeof raw === 'string' && raw.trim() !== '' ? raw.trim() : null;
  }, [searchParams]);
  const isProduct = variant === 'product';
  const { user } = useAuth();
  const credits = useCredits();
  const trialGenerationUsed = useTrialGenerationUsed();
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const [uploadedFile, setUploadedFile] = useState(/** @type {File | null} */ (null));
  const [floorPlanFileName, setFloorPlanFileName] = useState('');
  const [preparedBlob, setPreparedBlob] = useState(null);
  const [preparedPreviewUrl, setPreparedPreviewUrl] = useState('');
  const [preparedMeta, setPreparedMeta] = useState(null);
  const [preparingImage, setPreparingImage] = useState(false);
  const [quality, setQuality] = useState(/** @type {'standard' | 'hq'} */ (QUALITY_STANDARD));
  const [outputType, setOutputType] = useState(FLOOR_PLAN_3D_OUTPUT_TYPE_DEFAULT);
  const [furnishingMode, setFurnishingMode] = useState(FLOOR_PLAN_3D_FURNISHING_DEFAULT);
  const [cameraView, setCameraView] = useState(FLOOR_PLAN_3D_CAMERA_VIEW_DEFAULT);
  const [outputTypePopupOpen, setOutputTypePopupOpen] = useState(false);
  const [cameraViewPopupOpen, setCameraViewPopupOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState(DESIGN_STYLES[0]);
  const [userNotes, setUserNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [creditsError, setCreditsError] = useState(false);
  const [resultImageUrl, setResultImageUrl] = useState('');
  const {
    pageMode,
    resultViewMode,
    setResultViewMode,
    currentProject,
    setCurrentProject,
    enterOutputMode,
    enterEditMode,
    resetToDesign,
    isEditMode,
  } = useDesignWorkspaceMode();
  const [hydratedEditId, setHydratedEditId] = useState(null);
  const [editHydration, setEditHydration] = useState(
    () => (searchParams.get('edit')?.trim() ? 'loading' : 'idle'),
  );
  const [downloadError, setDownloadError] = useState('');
  const [zoomedImage, setZoomedImage] = useState(
    /** @returns {{ src: string; alt: string } | null} */
    () => null,
  );
  const preparedUrlRef = useRef('');
  const rawSourceFileRef = useRef(/** @type {File | null} */ (null));
  const projectPaddingAppliedRef = useRef(false);
  const skipCameraReprepRef = useRef(false);

  const imageQualityOptions = useMemo(
    () => [
      {
        value: QUALITY_STANDARD,
        label: 'Standard image quality',
        cost: trialGenerationUsed === false ? 0 : 2,
      },
      { value: QUALITY_HQ, label: 'High image quality', cost: 4 },
    ],
    [trialGenerationUsed],
  );

  const creditCost = effectiveCreditCost(quality, trialGenerationUsed);
  const insufficientCredits =
    creditCost > 0 && credits !== null && credits < creditCost;

  const selectedOutputTypeOption = useMemo(
    () =>
      FLOOR_PLAN_3D_OUTPUT_TYPE_OPTIONS.find((option) => option.value === outputType) ??
      FLOOR_PLAN_3D_OUTPUT_TYPE_OPTIONS[0],
    [outputType],
  );

  const selectedCameraViewOption = useMemo(
    () =>
      FLOOR_PLAN_3D_CAMERA_VIEW_OPTIONS.find((option) => option.value === cameraView) ??
      FLOOR_PLAN_3D_CAMERA_VIEW_OPTIONS[0],
    [cameraView],
  );

  useEffect(() => {
    if (!cameraViewPopupOpen && !outputTypePopupOpen) return undefined;
    const onDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        setCameraViewPopupOpen(false);
        setOutputTypePopupOpen(false);
      }
    };
    document.addEventListener('keydown', onDocumentKeyDown);
    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [cameraViewPopupOpen, outputTypePopupOpen]);

  const revokePreparedUrl = useCallback(() => {
    if (preparedUrlRef.current) {
      URL.revokeObjectURL(preparedUrlRef.current);
      preparedUrlRef.current = '';
    }
  }, []);

  useEffect(() => {
    return () => {
      revokePreparedUrl();
    };
  }, [revokePreparedUrl]);

  const resetGenerationResults = useCallback(() => {
    setResultImageUrl('');
  }, []);

  const runPrepareFromFile = useCallback(
    async (file, { isNewUpload = false, skipPadding = false } = {}) => {
      revokePreparedUrl();
      setPreparedBlob(null);
      setPreparedPreviewUrl('');
      setPreparedMeta(null);
      setError('');
      setCreditsError(false);
      resetGenerationResults();
      setPreparingImage(true);

      if (isNewUpload && file instanceof File) {
        rawSourceFileRef.current = file;
        projectPaddingAppliedRef.current = false;
        skipCameraReprepRef.current = true;
      }

      try {
        const result = await prepareFloorPlanTo3dInputImage(file, {
          cameraView,
          quality,
          skipPadding,
        });
        preparedUrlRef.current = result.objectUrl;
        setPreparedBlob(result.blob);
        setPreparedPreviewUrl(result.objectUrl);
        setPreparedMeta(result.meta);
        if (skipPadding) {
          projectPaddingAppliedRef.current = true;
        }
      } catch (err) {
        setPreparedBlob(null);
        setPreparedPreviewUrl('');
        setPreparedMeta(null);
        setError(err instanceof Error ? err.message : 'Could not prepare image.');
      } finally {
        setPreparingImage(false);
      }
    },
    [revokePreparedUrl, resetGenerationResults, cameraView, quality],
  );

  const applyUploadedFile = useCallback(
    async (file, options = {}) => {
      const isNewUpload = options.isNewUpload !== false;
      setFloorPlanFileName(file instanceof File ? file.name : 'floor-plan.jpg');
      setUploadedFile(file instanceof File ? file : null);
      await runPrepareFromFile(file, {
        isNewUpload,
        skipPadding: options.skipPadding === true,
      });
    },
    [runPrepareFromFile],
  );
  const applyUploadedFileRef = useRef(applyUploadedFile);
  applyUploadedFileRef.current = applyUploadedFile;

  useEffect(() => {
    if (!rawSourceFileRef.current) {
      if (projectPaddingAppliedRef.current) {
        console.warn(
          '[FloorPlan3D] Raw source unavailable; cannot re-pad for new camera view. Using stored prepared image.',
        );
      }
      return;
    }
    if (skipCameraReprepRef.current) {
      skipCameraReprepRef.current = false;
      return;
    }
    void runPrepareFromFile(rawSourceFileRef.current, { isNewUpload: false });
  }, [cameraView, quality, runPrepareFromFile]);

  const handleFileChange = useCallback(
    (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (file.size > MAX_UPLOAD_BYTES) {
        setError('File is too large. Please upload a JPG or PNG up to 10MB.');
        event.target.value = '';
        return;
      }

      applyUploadedFile(file);
      event.target.value = '';
    },
    [applyUploadedFile],
  );

  const handleDrop = useCallback(
    (event) => {
      event.preventDefault();
      if (generating || preparingImage) return;
      const file = event.dataTransfer.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        setError('Please upload a JPG or PNG image.');
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setError('File is too large. Please upload a JPG or PNG up to 10MB.');
        return;
      }
      applyUploadedFile(file);
    },
    [applyUploadedFile, generating, preparingImage],
  );

  const handleOutputEdit = useCallback(() => {
    if (currentProject?.id) setHydratedEditId(String(currentProject.id));
    enterEditMode();
  }, [currentProject, enterEditMode]);

  useEffect(() => {
    if (!editFromUrl) {
      setHydratedEditId(null);
      setEditHydration('idle');
      return undefined;
    }
    if (!user?.uid) return undefined;

    let cancelled = false;

    async function hydrateEditProject() {
      setEditHydration('loading');
      rawSourceFileRef.current = null;
      projectPaddingAppliedRef.current = false;
      skipCameraReprepRef.current = true;
      try {
        const snap = await getDoc(doc(db, 'projects', editFromUrl));
        if (cancelled) return;
        if (!snap.exists()) {
          navigate('/projects', { replace: true });
          return;
        }
        const data = snap.data();
        if (data.userId !== user.uid) {
          navigate('/projects', { replace: true });
          return;
        }
        const preparedUrl =
          (typeof data.preparedInputImageUrl === 'string' && data.preparedInputImageUrl.trim()) ||
          (typeof data.originalImageUrl === 'string' && data.originalImageUrl.trim()) ||
          '';
        if (!preparedUrl) {
          navigate('/projects', { replace: true });
          return;
        }
        const paddingAppliedOnProject = data.paddingApplied === true;
        const rawOriginalUrl =
          typeof data.rawOriginalImageUrl === 'string' ? data.rawOriginalImageUrl.trim() : '';

        const projectOriginalApiPath = `/api/projects/${encodeURIComponent(editFromUrl)}/original`;
        const projectOriginalApiUrl =
          HAS_API_URL && API_BASE_URL
            ? process.env.NODE_ENV === 'development'
              ? projectOriginalApiPath
              : `${API_BASE_URL}${projectOriginalApiPath}`
            : null;

        const blob = await fetchProjectOriginalBlob({
          user,
          projectId: editFromUrl,
          projectData: data,
          fallbackUrl: preparedUrl,
          projectOriginalApiUrl,
        });

        if (cancelled) return;

        const type =
          blob.type && blob.type.startsWith('image/') ? blob.type : 'image/jpeg';
        const ext = type.includes('png') ? '.png' : '.jpg';
        const preparedFile = new File([blob], `floor-plan-prepared${ext}`, { type });

        if (rawOriginalUrl) {
          try {
            const rawRes = await fetch(rawOriginalUrl);
            if (rawRes.ok) {
              const rawBlob = await rawRes.blob();
              if (rawBlob?.size) {
                const rawType =
                  rawBlob.type && rawBlob.type.startsWith('image/')
                    ? rawBlob.type
                    : 'image/jpeg';
                rawSourceFileRef.current = new File(
                  [rawBlob],
                  `floor-plan-raw${rawType.includes('png') ? '.png' : '.jpg'}`,
                  { type: rawType },
                );
              }
            }
          } catch {
            rawSourceFileRef.current = null;
          }
        }

        setOutputType(
          pickOptionValue(
            data.outputType,
            FLOOR_PLAN_3D_OUTPUT_TYPE_OPTIONS,
            FLOOR_PLAN_3D_OUTPUT_TYPE_DEFAULT,
          ),
        );
        setFurnishingMode(
          pickOptionValue(
            data.furnishingMode,
            FLOOR_PLAN_3D_FURNISHING_OPTIONS,
            FLOOR_PLAN_3D_FURNISHING_DEFAULT,
          ),
        );
        setCameraView(
          pickOptionValue(
            normalizeFloorPlan3dCameraViewForUi(data.cameraView),
            FLOOR_PLAN_3D_CAMERA_VIEW_OPTIONS,
            FLOOR_PLAN_3D_CAMERA_VIEW_DEFAULT,
          ),
        );
        setSelectedStyle(
          DESIGN_STYLES.includes(data.style) ? data.style : DESIGN_STYLES[0],
        );
        setUserNotes(
          typeof data.notes === 'string' ? data.notes.trim().slice(0, NOTES_MAX) : '',
        );
        setQuality(data.imageQuality === 'high' ? QUALITY_HQ : QUALITY_STANDARD);

        skipCameraReprepRef.current = true;
        if (paddingAppliedOnProject) {
          projectPaddingAppliedRef.current = true;
          await applyUploadedFileRef.current(preparedFile, {
            isNewUpload: false,
            skipPadding: true,
          });
        } else {
          await applyUploadedFileRef.current(preparedFile, { isNewUpload: true });
        }
        if (cancelled) return;

        if (data.generatedImageUrl) {
          setCurrentProject({ id: snap.id, ...data });
        }
        setHydratedEditId(editFromUrl);
        setEditHydration('ready');
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load this project for editing.');
          navigate('/projects', { replace: true });
        }
      }
    }

    void hydrateEditProject();
    return () => {
      cancelled = true;
    };
  }, [editFromUrl, user, navigate, setCurrentProject]);

  const handleGenerate = useCallback(async () => {
    if (!user) {
      navigate(`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`);
      return;
    }
    if (!preparedBlob) {
      setError('Upload a floor plan image before generating.');
      return;
    }
    if (insufficientCredits) {
      setCreditsError(true);
      return;
    }

    setGenerating(true);
    setError('');
    setCreditsError(false);
    resetGenerationResults();

    console.log('[floorPlanTo3d request]', {
      selectedOutputType: outputType,
      selectedCameraView: cameraView,
      selectedFurnishingMode: furnishingMode,
    });

    const existingProjectId =
      hydratedEditId || (currentProject?.id ? String(currentProject.id) : null);
    const projectId = existingProjectId || doc(collection(db, 'projects')).id;

    const result = await requestFloorPlanTo3DImage(user, {
      blob: preparedBlob,
      quality,
      outputType,
      cameraView,
      furnishingMode,
      style: selectedStyle,
      userNotes: userNotes.trim(),
      projectId,
      preparedMeta: preparedMeta
        ? {
            preparedWidth: preparedMeta.preparedWidth,
            preparedHeight: preparedMeta.preparedHeight,
            paddingRatio: preparedMeta.paddingRatio,
            paddingApplied: preparedMeta.paddingApplied,
            frontendPrepared: preparedMeta.frontendPrepared,
          }
        : undefined,
      rawSourceFile: rawSourceFileRef.current,
    });

    setGenerating(false);

    if (!result.ok) {
      if (result.code === 'INSUFFICIENT_CREDITS') {
        setCreditsError(true);
      }
      setError(result.error);
      return;
    }

    setResultImageUrl(result.imageDataUrl);
    const completedProject = result.project
      ? { id: projectId, ...result.project }
      : {
          id: projectId,
          toolType: 'floorplan_2d_to_3d',
          status: 'completed',
          generatedImageUrl: result.generatedImageUrl || '',
          originalImageUrl: '',
          style: selectedStyle,
          notes: userNotes.trim(),
          outputType,
          cameraView,
          furnishingMode,
          imageQuality: quality === QUALITY_HQ ? 'high' : 'standard',
        };
    if (result.generatedImageUrl) {
      completedProject.generatedImageUrl = result.generatedImageUrl;
    }
    if (existingProjectId) {
      setHydratedEditId(existingProjectId);
    }
    enterOutputMode(completedProject);
  }, [
    user,
    preparedBlob,
    preparedMeta,
    quality,
    outputType,
    cameraView,
    furnishingMode,
    selectedStyle,
    userNotes,
    insufficientCredits,
    resetGenerationResults,
    hydratedEditId,
    currentProject,
    enterOutputMode,
    location.pathname,
    location.search,
    navigate,
  ]);

  const editRouteBlocking = Boolean(editFromUrl) && editHydration !== 'ready';
  const canGenerate =
    Boolean(preparedBlob) &&
    !preparingImage &&
    !generating &&
    !insufficientCredits &&
    !editRouteBlocking;
  const previewDisplayUrl = resultImageUrl || preparedPreviewUrl || '';
  const showOutputResultViewer = pageMode === 'output' && currentProject != null;
  const editSourcePreviewUrl = resolveEditSourcePreviewUrl({
    preparedPreviewUrl,
    project: currentProject,
  });
  const showWorkspaceResultPanel = Boolean(
    (showOutputResultViewer || isEditMode) && currentProject,
  );
  const showInlinePreview = !showWorkspaceResultPanel;
  const showWorkspaceGuide =
    !showWorkspaceResultPanel && !editSourcePreviewUrl && !generating && !preparingImage;
  const showPreparedDetails = Boolean(preparedPreviewUrl && !resultImageUrl && preparedMeta && preparedBlob);
  const previewDisplayAlt = resultImageUrl
    ? 'Generated 3D floor plan visualization'
    : 'Prepared 2D floor plan';
  const pageHeading =
    pageMode === 'output' && currentProject ? (
      <h1 className="tool-hub-heading">{buildProjectTitle(currentProject)}</h1>
    ) : (
      <h1 className="tool-hub-heading">
        {publicSeo?.h1 ? (
          publicSeo.h1
        ) : isProduct ? (
          <>
            2D Plan to <span className="new-design-page-heading-accent">3D View</span>
          </>
        ) : (
          <>
            2D Floor Plan to <span className="new-design-page-heading-accent">3D Image</span>
          </>
        )}
      </h1>
    );
  const pageHeadingSubtext = publicSeo?.headingSubtext || '';

  return (
    <>
      <Seo
        title={
          publicSeo?.title ||
          (isProduct
            ? '2D Plan to 3D View | MyRoomsAI'
            : '2D Floor Plan to 3D Image — Test')
        }
        description={
          publicSeo?.description ||
          (isProduct
            ? 'Upload a 2D floor plan and convert it into a 3D top-view image.'
            : 'Convert a 2D architectural floor plan into a 3D visualization image.')
        }
        canonicalPath={
          publicSeo?.path || (isProduct ? '/projects/ai-2d-floorplan-to-3d' : '/test/floorplan-to-3d-image')
        }
        noindex={!publicSeo}
        structuredData={
          publicSeo
            ? [...buildPublicToolPageSchemas(publicSeo), getSeoFaqSchema(publicSeo.path)].filter(Boolean)
            : []
        }
      />
      {!isInsideAppLayout ? <Navbar /> : null}
      <main
        className={`dashboard-page new-design-page fp3d-test-page${
          pageMode === 'output' ? ' project-detail-page' : ''
        }`}
      >
        <div className="container">
          {insufficientCredits || creditsError ? (
            <p className="dashboard-inline-error" role="alert">
              You do not have enough credits.{' '}
              <Link to="/credits">Buy a credit pack</Link> to continue.
            </p>
          ) : null}

          {error && !creditsError && !insufficientCredits ? (
            <p className="dashboard-inline-error" role="alert">
              {error}
            </p>
          ) : null}

          {editHydration === 'loading' ? (
            <p className="project-page-muted" role="status">
              Loading project…
            </p>
          ) : null}

          {pageMode === 'output' && currentProject ? (
            <>
              <DesignOutputWorkspace
                project={currentProject}
                resultViewMode={resultViewMode}
                onResultViewModeChange={setResultViewMode}
                onNewProject={resetToDesign}
                onEdit={handleOutputEdit}
                shopItemCount={0}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                downloadError={downloadError}
                enableWideImageStretch
              />
            </>
          ) : (
          <section
            className={`dashboard-grid new-design-workspace fp3d-test-workspace design-dual-layout${
              isEditMode ? ' edit-mode' : ''
            }${showInlinePreview ? ' new-design-workspace--preview-heading' : ''}`}
          >
            <article className="card new-design-setup-card fp3d-test-setup-card">
                <>
              <div className="new-design-upload-mobile-summary fp3d-test-upload-section">
                <h3 className="new-design-subheading new-design-upload-section-heading">
                  <Upload
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Upload Floor Plan
                </h3>

                <input
                  ref={fileInputRef}
                  id="fp3d-floor-plan-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileChange}
                  className="new-design-file-input"
                  disabled={generating || preparingImage}
                />

                {!uploadedFile ? (
                  <div className="new-design-upload-dropzone-wrap">
                    <label
                      htmlFor="fp3d-floor-plan-upload"
                      className="upload-dropzone fp3d-test-upload-dropzone"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={handleDrop}
                    >
                      <div className="upload-dropzone-inner">
                        <span className="upload-dropzone-cloud" aria-hidden="true">
                          <Upload size={38} strokeWidth={1.75} />
                        </span>
                        <span>Upload your floor plan</span>
                      </div>
                    </label>
                  </div>
                ) : (
                  <>
                    {preparedPreviewUrl ? (
                      <div className="fp3d-test-upload-preview">
                        <img
                          src={preparedPreviewUrl}
                          alt="Uploaded floor plan preview"
                          className="fp3d-test-upload-preview-img"
                          decoding="async"
                        />
                      </div>
                    ) : preparingImage ? (
                      <p className="new-design-field-helper" role="status">
                        Preparing image…
                      </p>
                    ) : null}
                    <div className="new-design-upload-mobile-summary-btns">
                      <div className="new-design-upload-summary-btn-row">
                        <button
                          type="button"
                          className="btn btn-ghost new-design-compact-ghost-btn new-design-upload-summary-btn"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={generating || preparingImage}
                        >
                          <ImageIcon size={15} strokeWidth={2} aria-hidden="true" />
                          Change Floor Plan
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="new-design-setup-fields fp3d-test-options-fields">
                <div className="new-design-control-group fp3d-test-option-card-section">
                  <h3 className="new-design-subheading" id="fp3d-output-type-label">
                    <SlidersHorizontal
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Render Style
                  </h3>
                  <div className="fp3d-test-option-card-select">
                    <button
                      type="button"
                      id="fp3d-output-type-trigger"
                      className="new-design-select-trigger"
                      aria-haspopup="dialog"
                      aria-expanded={outputTypePopupOpen}
                      aria-controls="fp3d-output-type-popup"
                      aria-labelledby="fp3d-output-type-label fp3d-output-type-trigger"
                      disabled={generating}
                      onClick={() => {
                        if (!generating) setOutputTypePopupOpen(true);
                      }}
                    >
                      <span className="new-design-select-trigger-label">
                        {selectedOutputTypeOption.label}
                      </span>
                      <ChevronDown size={18} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <SlidersHorizontal
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Furnishing
                  </h3>
                  <NewDesignSelect
                    id="fp3d-furnishing"
                    value={furnishingMode}
                    onChange={setFurnishingMode}
                    disabled={generating}
                    options={FLOOR_PLAN_3D_FURNISHING_OPTIONS}
                  />
                </div>

                <div className="new-design-control-group fp3d-test-option-card-section">
                  <h3 className="new-design-subheading" id="fp3d-camera-view-label">
                    <Box
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    3D View Angle
                  </h3>
                  <div className="fp3d-test-option-card-select">
                    <button
                      type="button"
                      id="fp3d-camera-view-trigger"
                      className="new-design-select-trigger"
                      aria-haspopup="dialog"
                      aria-expanded={cameraViewPopupOpen}
                      aria-controls="fp3d-camera-view-popup"
                      aria-labelledby="fp3d-camera-view-label fp3d-camera-view-trigger"
                      disabled={generating}
                      onClick={() => {
                        if (!generating) setCameraViewPopupOpen(true);
                      }}
                    >
                      <span className="new-design-select-trigger-label">
                        {selectedCameraViewOption.label}
                      </span>
                      <ChevronDown size={18} strokeWidth={2} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <SlidersHorizontal
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Interior Style
                  </h3>
                  <NewDesignSelect
                    id="fp3d-style"
                    className="new-design-style-select"
                    value={selectedStyle}
                    onChange={setSelectedStyle}
                    disabled={generating}
                    options={DESIGN_STYLES}
                  />
                </div>

                <div className="new-design-control-group">
                  <h3 className="new-design-subheading">
                    <SlidersHorizontal
                      className="new-design-subheading-icon"
                      size={18}
                      strokeWidth={2}
                      aria-hidden
                    />
                    Additional notes
                  </h3>
                  <textarea
                    id="fp3d-user-notes"
                    className="dashboard-notes new-design-notes"
                    rows={2}
                    maxLength={NOTES_MAX}
                    value={userNotes}
                    onChange={(e) => setUserNotes(e.target.value.slice(0, NOTES_MAX))}
                    placeholder="E.g. light color scheme, wooden flooring, modern furniture..."
                    disabled={generating}
                  />
                  <p className="new-design-char-count" aria-live="polite">
                    {userNotes.length}/{NOTES_MAX}
                  </p>
                </div>
              </div>

              <div className="new-design-control-group new-design-setup-quality-group fp3d-test-quality-section">
                <h3 className="new-design-subheading" id="fp3d-quality-label">
                  <ImageIcon
                    className="new-design-subheading-icon"
                    size={18}
                    strokeWidth={2}
                    aria-hidden
                  />
                  Image quality
                </h3>
                <NewDesignSelect
                  id="fp3d-image-quality-select"
                  aria-labelledby="fp3d-quality-label"
                  value={quality}
                  disabled={generating}
                  onChange={setQuality}
                  options={imageQualityOptions}
                />
              </div>

              {generating ? (
                <p className="new-2d-project-loading fp3d-test-loading" role="status">
                  Generating your 3D floor plan image… This may take up to a minute.
                </p>
              ) : null}

              <div className="new-design-setup-generate-group">
                <button
                  type="button"
                  className="btn btn-primary full-width new-design-generate-btn fp3d-test-generate-btn"
                  onClick={handleGenerate}
                  disabled={!canGenerate}
                >
                  <Sparkles size={25} strokeWidth={2} aria-hidden="true" />
                  {generating ? 'Generating…' : 'Generate 3D Image'}
                </button>
                {insufficientCredits ? (
                  <div className="new-design-no-credits" role="status">
                    <span>You do not have enough credits.</span>
                    <Link to="/credits" className="btn btn-outline new-design-get-credit-btn">
                      Get credit
                    </Link>
                  </div>
                ) : null}
              </div>
                </>
            </article>

            {showWorkspaceResultPanel ? (
              <DesignResultViewer
                project={currentProject}
                editSourceImageUrl={isEditMode ? editSourcePreviewUrl : null}
                editSourceImageAlt="Prepared 2D floor plan"
                resultViewMode={resultViewMode}
                onDownloadImage={async (url, baseName) => {
                  setDownloadError('');
                  try {
                    await downloadUrlAsFile(url, baseName, user);
                  } catch (err) {
                    setDownloadError(err?.message || 'Download failed.');
                  }
                }}
                onZoomImage={setZoomedImage}
                previewLayout="plain"
                showMobileActions={false}
                showShopOnMobile={false}
                enableWideImageStretch
              />
            ) : (
            <div
              className={`new-design-workspace-preview fp3d-test-preview${
                previewDisplayUrl ? ' new-design-workspace-preview--has-image new-design-workspace-preview--plain' : ''
              }${showWorkspaceGuide ? ' new-design-workspace-preview--has-guide' : ''}${
                generating || preparingImage ? ' new-design-workspace-preview--submitting' : ''
              }`}
              aria-label="Floor plan preview"
            >
              <div className="new-design-workspace-preview-heading-group">
                {pageHeading}
                {pageHeadingSubtext && showInlinePreview ? (
                  <p className="new-design-workspace-preview-heading-subtext">{pageHeadingSubtext}</p>
                ) : null}
                {showWorkspaceGuide ? (
                  <p className="new-design-workspace-preview-tool-hint">{WORKSPACE_PREVIEW.hint}</p>
                ) : null}
              </div>
              {showWorkspaceGuide ? (
                <div className="new-design-workspace-preview-guide">
                  <div className="new-design-workspace-preview-tool-media new-design-workspace-preview-tool-media--vertical-scan fp3d-test-preview-tool-media--reverse-scan">
                    <div className="new-design-workspace-preview-offset-rectangle" aria-hidden="true" />
                    <VerticalAiBeforeAfterSlider
                      beforeImage={WORKSPACE_PREVIEW.beforeImage}
                      afterImage={WORKSPACE_PREVIEW.afterImage}
                      beforeAlt={`${WORKSPACE_PREVIEW.alt} before`}
                      afterAlt={`${WORKSPACE_PREVIEW.alt} after`}
                      fallbackSrc={WORKSPACE_PREVIEW.afterImage}
                      showLabels
                      beforeLabel="After"
                      afterLabel="Before"
                    />
                  </div>
                </div>
              ) : null}
              {preparingImage ? (
                <div className="new-design-workspace-preview-content">
                  <div className="generate-waiting-indicator" role="status" aria-live="polite">
                    <div className="generate-waiting-text">Preparing your floor plan image…</div>
                    <div className="generate-progress-track" aria-hidden="true">
                      <div className="generate-progress-fill" />
                    </div>
                  </div>
                </div>
              ) : null}
              {!preparingImage && (previewDisplayUrl || generating) ? (
                <>
                  {previewDisplayUrl ? (
                    <img
                      src={previewDisplayUrl}
                      alt={previewDisplayAlt}
                      className="new-design-workspace-preview-img"
                      decoding="async"
                      onLoad={(event) => {
                        applyPreviewImageLayoutClasses(event.currentTarget);
                      }}
                    />
                  ) : null}
                  {showPreparedDetails ? (
                    <div className="fp3d-test-preview-details">
                      <p className="fp3d-test-upload-name">{floorPlanFileName}</p>
                      <p className="new-design-field-helper">
                        Original: {preparedMeta.originalWidth}×{preparedMeta.originalHeight} px
                      </p>
                      <p className="new-design-field-helper">
                        Prepared: {preparedMeta.preparedWidth}×{preparedMeta.preparedHeight} px ·{' '}
                        {formatFileSize(preparedBlob.size)} · JPEG · 90% quality
                      </p>
                    </div>
                  ) : null}
                  {generating ? (
                    <div className="generate-waiting-indicator" role="status" aria-live="polite">
                      <div className="generate-waiting-text">
                        Generating your 3D floor plan image… please wait
                      </div>
                      <div className="generate-progress-track" aria-hidden="true">
                        <div className="generate-progress-fill" />
                      </div>
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
            )}
          </section>
          )}
          {publicSeo ? <ToolSeoSections {...publicSeo} /> : null}
          {publicSeo ? <CollapsibleSeoContent {...getSeoContent(publicSeo.path)} /> : null}
        </div>
      </main>
      {zoomedImage ? (
        <div
          className="image-lightbox-backdrop"
          role="presentation"
          onClick={() => setZoomedImage(null)}
        >
          <div
            className="image-lightbox-content"
            role="dialog"
            aria-modal="true"
            aria-label="Image preview"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="image-lightbox-close"
              aria-label="Close image preview"
              onClick={() => setZoomedImage(null)}
            >
              Close
            </button>
            <img
              src={zoomedImage.src}
              alt={zoomedImage.alt}
              className="image-lightbox-img"
              loading="eager"
              decoding="sync"
            />
          </div>
        </div>
      ) : null}
      {outputTypePopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => setOutputTypePopupOpen(false)}
        >
          <div
            id="fp3d-output-type-popup"
            className="new-design-theme-popup new-design-theme-popup--furniture new-design-theme-popup--fp3d-card-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fp3d-output-type-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="fp3d-output-type-popup-title" className="new-design-theme-popup-title">
                Render Style
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                onClick={() => setOutputTypePopupOpen(false)}
              >
                Close
              </button>
            </div>
            <div
              className="new-design-furniture-grid fp3d-test-option-card-modal-grid"
              role="group"
              aria-label="Render style options"
            >
              {FLOOR_PLAN_3D_OUTPUT_TYPE_OPTIONS.map((option) => {
                const isSelected = outputType === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`new-design-furniture-tile fp3d-test-option-card-tile${
                      isSelected ? ' new-design-furniture-tile--active' : ''
                    }`}
                    aria-pressed={isSelected}
                    aria-label={option.label}
                    onClick={() => {
                      setOutputType(option.value);
                      setOutputTypePopupOpen(false);
                    }}
                  >
                    <img
                      src={option.image}
                      alt=""
                      className="fp3d-test-option-card-thumb"
                      loading="lazy"
                      decoding="async"
                      aria-hidden="true"
                    />
                    <span className="new-design-furniture-tile-label">
                      {option.cardLabel ?? option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
      {cameraViewPopupOpen ? (
        <div
          className="new-design-theme-popup-backdrop"
          role="presentation"
          onClick={() => setCameraViewPopupOpen(false)}
        >
          <div
            id="fp3d-camera-view-popup"
            className="new-design-theme-popup new-design-theme-popup--furniture new-design-theme-popup--fp3d-card-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby="fp3d-camera-view-popup-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="new-design-theme-popup-head">
              <h2 id="fp3d-camera-view-popup-title" className="new-design-theme-popup-title">
                3D View Angle
              </h2>
              <button
                type="button"
                className="btn btn-ghost new-design-theme-popup-close"
                onClick={() => setCameraViewPopupOpen(false)}
              >
                Close
              </button>
            </div>
            <div
              className="new-design-furniture-grid fp3d-test-option-card-modal-grid"
              role="group"
              aria-label="3D view angle options"
            >
              {getFloorPlan3dCameraViewPopupOptions().map((option) => {
                const isSelected = cameraView === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    className={`new-design-furniture-tile fp3d-test-option-card-tile${
                      option.value === 'top-down-view'
                        ? ' fp3d-test-option-card-tile--centered'
                        : ''
                    }${isSelected ? ' new-design-furniture-tile--active' : ''}`}
                    aria-pressed={isSelected}
                    aria-label={option.label}
                    onClick={() => {
                      setCameraView(option.value);
                      setCameraViewPopupOpen(false);
                    }}
                  >
                    <img
                      src={option.image}
                      alt=""
                      className="fp3d-test-option-card-thumb"
                      loading="lazy"
                      decoding="async"
                      aria-hidden="true"
                    />
                    <span className="new-design-furniture-tile-label">{option.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
      {!isInsideAppLayout ? <Footer /> : null}
    </>
  );
}

export default FloorPlanTo3DImageTestPage;
